import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { assets, utils, dialogs, i18next } from '@yola/ws-sdk';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import UploadImageContainer from 'src/js/modules/common/containers/upload-image-container';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getFileName from 'src/js/modules/website-settings/helpers/get-file-name';
import constants from '../constants';
import SearchPreview from './search-preview';

import ChooseImageButton from './choose-image-button';
import getImageFileSizeFromUrl from 'src/js/modules/utils/get-image-file-size-from-url';

const {
  Stack,
  Heading,
  ControlGroup,
  CheckboxGroup,
  InputGroupField,
  CheckboxField,
  TextAreaField,
  FieldStatus,
} = designSystem;
const { useWatch } = reactHookForm;
const { fieldNames } = constants;
const MAX_OPEN_GRAPH_IMAGE_SIZE_BYTES = 1024 * 1024;

function OpenGraphSection({
  control,
  fieldRefs,
  captions,
  pathPrefix,
  businessName,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled,
  showPageSettingsDialog,
  onOpenGraphImageChange,
  onChangeSameAsPageTitle,
  onChangeSameAsMetaDescription,
}) {
  const [
    pagePathValue,
    openGraphTitle,
    openGraphDescription,
    openGraphImage,
    sameAsPageTitle,
    sameAsMetaDescription,
    displayBusinessName,
  ] = useWatch({
    control,
    name: [
      fieldNames.PATH,
      fieldNames.OG_TITLE,
      fieldNames.OG_DESCRIPTION,
      fieldNames.OG_IMAGE,
      fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE,
      fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION,
      fieldNames.DISPLAY_BUSINESS_NAME,
    ],
  });

  const path = `${pathPrefix}${pagePathValue}`;
  let previewTitle = openGraphTitle;

  if (
    isBusinessInfoInWebsiteSettingsFeatureFlagEnabled &&
    sameAsPageTitle &&
    businessName &&
    displayBusinessName
  ) {
    const unescapedBusinessName = utils.unescapeHTML(businessName);

    if (previewTitle) {
      previewTitle = `${previewTitle} | ${unescapedBusinessName}`;
    } else {
      previewTitle = unescapedBusinessName;
    }
  }

  if (!previewTitle) {
    previewTitle = captions.preview.title;
  }

  const previewDescription = openGraphDescription?.trim()
    ? openGraphDescription
    : captions.preview.description;
  let previewImage = '';
  if (openGraphImage) {
    previewImage = utils.validation.isURL(openGraphImage)
      ? openGraphImage
      : assets.helpers.addBaseHref(openGraphImage);
  }

  const handleRemoveImage = useCallback(() => {
    onOpenGraphImageChange('');
    showPageSettingsDialog({
      activeTabId: constants.tabIdentifiers.OPEN_GRAPH,
      formData: {
        [fieldNames.OG_IMAGE]: '',
      },
    });
  }, [onOpenGraphImageChange, showPageSettingsDialog]);

  const handleDialogCancel = useCallback(() => {
    showPageSettingsDialog({
      activeTabId: constants.tabIdentifiers.OPEN_GRAPH,
    });
  }, [showPageSettingsDialog]);

  const handleUrlPhotoLoadErrorDismiss = useCallback(() => {
    dialogs.operations.hide({ preserveSharedData: true });
    showPageSettingsDialog({
      activeTabId: constants.tabIdentifiers.OPEN_GRAPH,
    });
  }, [showPageSettingsDialog]);

  const showOpenGraphImageSizeLimitAlert = useCallback(() => {
    dialogs.operations.show(dialogTypes.ALERT_DIALOG, {
      glyph: 'alert-circle',
      height: 'auto',
      captions: {
        title: i18next.t('File is too large'),
        description: i18next.t(
          'Open Graph image must be 1MB or smaller. Please choose a smaller file.'
        ),
        dismiss: i18next.t('Dismiss'),
      },
      onDismiss: () => {
        dialogs.operations.hide({ preserveSharedData: true });
        showPageSettingsDialog({
          activeTabId: constants.tabIdentifiers.OPEN_GRAPH,
        });
      },
    });
  }, [showPageSettingsDialog]);

  const handleImageUploadWithSizeLimit = useCallback(
    (fileList, onImageUpload) => {
      const [imageFile] = fileList;

      if (!imageFile) {
        return;
      }

      if (imageFile.size > MAX_OPEN_GRAPH_IMAGE_SIZE_BYTES) {
        showOpenGraphImageSizeLimitAlert();
        return;
      }

      onImageUpload(fileList);
    },
    [showOpenGraphImageSizeLimitAlert]
  );

  const handleUrlPhotoUploadWithSizeLimit = useCallback(
    async (url, onUrlPhotoUpload) => {
      if (!url) {
        return;
      }

      const fullUrl = assets.helpers.addBaseHref(url);
      const [imageSize, sizeError] = await utils.handleAsyncError(
        getImageFileSizeFromUrl(fullUrl || url)
      );

      if (!sizeError && imageSize > MAX_OPEN_GRAPH_IMAGE_SIZE_BYTES) {
        showOpenGraphImageSizeLimitAlert();
        return;
      }

      onUrlPhotoUpload(url);
    },
    [showOpenGraphImageSizeLimitAlert]
  );

  return (
    <UploadImageContainer
      preserveSharedData
      imagePlacement={assets.constants.IMAGE_PLACEMENTS.OG_TAG}
      onUrlPhotoLoadErrorDismiss={handleUrlPhotoLoadErrorDismiss}
      onImageUploadEnd={({ source }) => {
        if (source) {
          onOpenGraphImageChange(source);
          showPageSettingsDialog({
            activeTabId: constants.tabIdentifiers.OPEN_GRAPH,
            formData: {
              [fieldNames.OG_IMAGE]: source,
            },
          });
        }
      }}
    >
      {({ onWrongFileType, onImageUpload, onStockPhotoUpload, onUrlPhotoUpload }) => (
        <Stack gap="spacing-m">
          <Stack gap="spacing-3-xs">
            <Heading type="heading-6">{captions.preview.heading}</Heading>
            <SearchPreview
              title={previewTitle}
              path={path}
              description={previewDescription}
              image={previewImage}
            />
          </Stack>

          <Stack gap="spacing-3-xs">
            <ControlGroup title={captions.title.name}>
              <div ref={fieldRefs[fieldNames.OG_TITLE]} className="ws-page-settings__field-wrapper">
                <InputGroupField
                  name={fieldNames.OG_TITLE}
                  control={control}
                  disabled={sameAsPageTitle}
                />
                <FieldStatus text={captions.title.description} />
              </div>
            </ControlGroup>

            <CheckboxGroup>
              <CheckboxField
                name={fieldNames.USE_PAGE_TITLE_FOR_OG_TITLE}
                control={control}
                label={captions.sameAsPageTitle.label}
                onChange={onChangeSameAsPageTitle}
              />
            </CheckboxGroup>
          </Stack>

          <Stack gap="spacing-3-xs">
            <ControlGroup title={captions.description.name}>
              <div
                ref={fieldRefs[fieldNames.OG_DESCRIPTION]}
                className="ws-page-settings__field-wrapper"
              >
                <TextAreaField
                  name={fieldNames.OG_DESCRIPTION}
                  control={control}
                  disabled={sameAsMetaDescription}
                  resize
                />
                <FieldStatus text={captions.description.description} />
              </div>
            </ControlGroup>

            <CheckboxGroup>
              <CheckboxField
                name={fieldNames.USE_META_DESCRIPTION_FOR_OG_DESCRIPTION}
                control={control}
                label={captions.sameAsMetaDescription.label}
                onChange={onChangeSameAsMetaDescription}
              />
            </CheckboxGroup>
          </Stack>

          <ControlGroup title={captions.image.name}>
            <div ref={fieldRefs[fieldNames.OG_IMAGE]} className="ws-page-settings__field-wrapper">
              <ChooseImageButton
                label={captions.image.button}
                placeholder={captions.image.empty}
                fileItemName={getFileName(openGraphImage) || openGraphImage}
                statusText={captions.image.description}
                onImageUpload={(fileList) =>
                  handleImageUploadWithSizeLimit(fileList, onImageUpload)
                }
                onStockPhotoUpload={onStockPhotoUpload}
                onWrongFileType={onWrongFileType}
                onUrlPhotoUpload={(url) => handleUrlPhotoUploadWithSizeLimit(url, onUrlPhotoUpload)}
                onUrlPhotoUploadCancel={handleDialogCancel}
                onStockPhotoDialogCancel={handleDialogCancel}
                onClear={handleRemoveImage}
              />
            </div>
          </ControlGroup>
        </Stack>
      )}
    </UploadImageContainer>
  );
}

OpenGraphSection.propTypes = {
  control: PropTypes.shape().isRequired,
  fieldRefs: PropTypes.shape().isRequired,
  captions: PropTypes.shape().isRequired,
  pathPrefix: PropTypes.string.isRequired,
  businessName: PropTypes.string.isRequired,
  isBusinessInfoInWebsiteSettingsFeatureFlagEnabled: PropTypes.bool.isRequired,
  showPageSettingsDialog: PropTypes.func.isRequired,
  onOpenGraphImageChange: PropTypes.func.isRequired,
  onChangeSameAsPageTitle: PropTypes.func.isRequired,
  onChangeSameAsMetaDescription: PropTypes.func.isRequired,
};

export default OpenGraphSection;
